<template>
  <LazyModal
    key="acknowledgement-modal"
    :is-visible="showAcknowledgementModal"
    no-cancel
    :tracking="{
      topicKey: 'acknowledgement',
    }"
    @confirm="handleConfirm"
  >
    <template #headline>
      <span class="flex text-left">
        {{ $t('acknowledgement.modal.title') }}
      </span>
    </template>

    <div>
      <i18n-t
        class="paragraph-regular text-left pb-200"
        keypath="acknowledgement.modal.text"
        tag="p"
      >
        <template #termsButton>
          <button
            class="terms-button text-link cursor-pointer"
            type="button"
            @click="handleClick(AcknowledgementPage.TermsAndConditions)"
          >
            {{ $t('acknowledgement.modal.termsButton') }}
          </button>
        </template>
        <template #privacyButton>
          <button
            class="privacy-button text-link cursor-pointer"
            type="button"
            @click="handleClick(AcknowledgementPage.PrivacyPolicy)"
          >
            {{ $t('acknowledgement.modal.privacyButton') }}
          </button>
        </template>
      </i18n-t>
    </div>

    <Illustration name="subtle" />
    <template #confirm>{{ $t('acknowledgement.modal.confirm') }}</template>

    <AcknowledgementFullscreenModal
      :key="fullScreenType"
      v-model:is-visible="isFullscreenVisible"
      :type="fullScreenType"
    />
  </LazyModal>
</template>

<script lang="ts" setup>
import { useAcknowledgement } from '../composables/acknowledgement'
import { AcknowledgementPage } from '../types'

const RETRY_MUTATION_DELAY_MS = 60_000

const { log } = useDatadog()
const { showAcknowledgementModal } = useAcknowledgement()

const { mutate } = useAcknowledgeTermsAndConditionsMutation()

const isFullscreenVisible = ref(false)
const fullScreenType = ref<AcknowledgementPage>(
  AcknowledgementPage.TermsAndConditions,
)

const handleClick = (type: AcknowledgementPage) => {
  fullScreenType.value = type
  isFullscreenVisible.value = true
}

const handleConfirm = async () => {
  try {
    await mutate()
  } catch (error) {
    setTimeout(() => mutate(), RETRY_MUTATION_DELAY_MS)

    log('error', {
      component: 'features/Acknowledgement/AcknowledgementModal.vue',
      error,
      message: 'Accepting terms and conditions failed',
    })
  }

  showAcknowledgementModal.value = false
}

watch(
  () => showAcknowledgementModal.value,
  (visible) => {
    if (typeof document !== 'undefined' && document.body) {
      document.body.style.overflow = visible ? 'hidden' : ''
    }
  },
  {
    immediate: true,
  },
)

onUnmounted(() => {
  if (typeof document !== 'undefined' && document.body) {
    document.body.style.overflow = ''
  }
})
</script>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
